import { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type { Dispatch, MutableRefObject, ReactNode, SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import UnsavedChangesModal from '../components/UnsavedChangesModal';

/** Von Seiten registrierter Save-Handler: false-Rückgabe oder Exception = Speichern fehlgeschlagen. */
export type SaveCallback = () => Promise<boolean | void> | boolean | void;

export interface UnsavedChangesContextType {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  handleNavigationAttempt: (to: string) => boolean;
  /**
   * Öffnet das (einzige, hier im Provider gerenderte) Unsaved-Changes-Modal.
   * `proceed` läuft nach erfolgreichem Speichern oder nach "Verwerfen";
   * "Zurück" verwirft die Aktion. Auslöser übergeben ihre Fortsetzung
   * (z.B. `() => navigate(to)` oder `() => applyStepChange(step)`) —
   * es gibt bewusst keine komponenten-eigenen Modal-Kopien mehr.
   */
  requestConfirmation: (proceed: () => void) => void;
  saveCallbackRef: MutableRefObject<SaveCallback | null>;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | undefined>(undefined);

// SECURITY FIX: Validate URL is local to prevent open redirect attacks
const isValidLocalUrl = (url: string | null | undefined): boolean => {
  if (!url || typeof url !== 'string') return false;
  // Only allow relative URLs starting with '/' but not '//'
  if (url.startsWith('/') && !url.startsWith('//')) return true;
  // Block any URL with protocol (http:, https:, javascript:, etc.)
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url)) return false;
  return false;
};

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const hasUnsavedChangesRef = useRef(false);
  // Die beim Öffnen des Modals hinterlegte Fortsetzung (siehe requestConfirmation).
  const pendingActionRef = useRef<(() => void) | null>(null);
  // Pages can register a save callback so the modal can trigger a real save.
  const saveCallbackRef = useRef<SaveCallback | null>(null);
  // navigate über Ref, damit die Callbacks unten mit useCallback([]) stabil
  // bleiben können (useNavigate garantiert keine stabile Identität).
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  });

  // Synchronisiere den Ref mit dem State
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  const requestConfirmation = useCallback((proceed: () => void) => {
    pendingActionRef.current = proceed;
    setShowModal(true);
  }, []);

  const handleNavigationAttempt = useCallback((to: string): boolean => {
    // SECURITY FIX: Validate URL to prevent open redirect
    if (!isValidLocalUrl(to)) {
      console.warn('SECURITY: Blocked invalid navigation URL:', to);
      return false;
    }

    const currentPath = window.location.pathname;
    // Seiten mit editierbaren Daten: Zeiten (Studierende), Planung (Lehrende),
    // Kursverwaltung (Lehrende). Verlassen mit ungespeicherten Änderungen fragt nach.
    const guardedPrefixes = ['/student', '/scheduling', '/me/classes'];
    const isOnGuardedPage = guardedPrefixes.some(
      p => currentPath === p || currentPath.startsWith(p + '/')
    );
    const targetPath = to.split(/[?#]/)[0];
    const isLeavingProcess = isOnGuardedPage && targetPath !== currentPath;

    if (hasUnsavedChangesRef.current && isLeavingProcess) {
      requestConfirmation(() => navigateRef.current(to));
      return false;
    }
    return true;
  }, [requestConfirmation]);

  // Abfangen des Browser-Back-Buttons
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChangesRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      const currentPath = window.location.pathname;
      const isInSchedulingProcess = currentPath === '/student' || currentPath === '/scheduling';

      if (isInSchedulingProcess && hasUnsavedChangesRef.current) {
        e.preventDefault();
        requestConfirmation(() => navigateRef.current(window.location.pathname));
        window.history.pushState(null, '', window.location.pathname);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [requestConfirmation]);

  // Modal-Handler: einmalig hier definiert — Seiten liefern nur noch ihre
  // Fortsetzung (requestConfirmation) und ggf. einen Save-Handler (saveCallbackRef).
  const handleModalSave = useCallback(async () => {
    if (saveCallbackRef.current) {
      try {
        const result = await saveCallbackRef.current();
        if (result === false) {
          // Speichern fehlgeschlagen — Modal offen lassen, nicht fortfahren
          return;
        }
      } catch (err) {
        console.error('Speichern aus dem Unsaved-Changes-Modal fehlgeschlagen:', err);
        return;
      }
    }
    setHasUnsavedChanges(false);
    setShowModal(false);
    const proceed = pendingActionRef.current;
    pendingActionRef.current = null;
    proceed?.();
  }, []);

  const handleModalDiscard = useCallback(() => {
    setHasUnsavedChanges(false);
    setShowModal(false);
    const proceed = pendingActionRef.current;
    pendingActionRef.current = null;
    proceed?.();
  }, []);

  const handleModalCancel = useCallback(() => {
    setShowModal(false);
    pendingActionRef.current = null;
  }, []);

  // I8: Provider-Value memoisieren. Setter (useState) und Refs sind stabil,
  // handleNavigationAttempt/requestConfirmation sind useCallback-stabil.
  const value = useMemo<UnsavedChangesContextType>(() => ({
    hasUnsavedChanges,
    setHasUnsavedChanges,
    handleNavigationAttempt,
    requestConfirmation,
    saveCallbackRef
  }), [hasUnsavedChanges, handleNavigationAttempt, requestConfirmation]);

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      {showModal && (
        <UnsavedChangesModal
          onSave={handleModalSave}
          onDiscard={handleModalDiscard}
          onCancel={handleModalCancel}
        />
      )}
    </UnsavedChangesContext.Provider>
  );
}

// Wie im JS-Original: kein Laufzeit-Guard — außerhalb des Providers wäre der
// Wert undefined. Der Cast erhält die bisherigen Consumer-Signaturen.
export const useUnsavedChanges = () => useContext(UnsavedChangesContext) as UnsavedChangesContextType;
